import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';

function yesterday() {
  return new Date(Date.now() - 86400000).toISOString().slice(0, 10);
}

function fmtDate(d) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function HoursBar({ hours }) {
  const max = 9;
  const pct = Math.min((hours / max) * 100, 100);
  const color = hours >= 7 ? '#16a34a' : hours >= 4.5 ? '#d97706' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
      <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 12, color, fontWeight: 600, minWidth: 36 }}>{hours}h</span>
    </div>
  );
}

function Section({ title, emoji, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', marginBottom: 20, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>{emoji}</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#073E81' }}>{title}</span>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

export default function DailyBriefPage() {
  const [date, setDate] = useState(yesterday());

  const { data, isLoading, error } = useQuery({
    queryKey: ['daily-brief', date],
    queryFn: () => api.get(`/daily-brief?date=${date}`).then(r => r.data),
  });

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#073E81', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Daily Brief</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111' }}>{fmtDate(date)}</h1>
        </div>
        <input
          type="date"
          value={date}
          max={yesterday()}
          onChange={e => setDate(e.target.value)}
          style={{ fontSize: 13, padding: '7px 12px', border: '1px solid #d0d0d0', borderRadius: 8, background: '#fff' }}
        />
      </div>

      {isLoading && <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading brief…</div>}
      {error && <div style={{ textAlign: 'center', padding: 60, color: '#dc2626' }}>Failed to load brief.</div>}

      {data && <>

        {/* Summary tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Present', value: data.totals.present, color: '#16a34a', bg: '#f0fdf4' },
            { label: 'Absent', value: data.totals.absent, color: '#dc2626', bg: '#fef2f2' },
            { label: 'Work Entries', value: data.totals.total_entries, color: '#073E81', bg: '#eff6ff' },
            { label: 'Tasks Done', value: data.totals.tasks_done, color: '#7c3aed', bg: '#f5f3ff' },
            { label: 'Overdue', value: data.totals.tasks_overdue, color: data.totals.tasks_overdue > 0 ? '#d97706' : '#16a34a', bg: data.totals.tasks_overdue > 0 ? '#fffbeb' : '#f0fdf4' },
          ].map(t => (
            <div key={t.label} style={{ background: t.bg, borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: t.color }}>{t.value}</div>
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{t.label}</div>
            </div>
          ))}
        </div>

        {/* Attendance */}
        <Section title="Attendance" emoji="🕐">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: '#999', fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600 }}>Clock In</th>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600 }}>Clock Out</th>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600, minWidth: 160 }}>Hours</th>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.attendance.map(a => (
                <tr key={a.id} style={{ borderTop: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '9px 0', fontWeight: 600, color: '#111' }}>{a.name}</td>
                  <td style={{ padding: '9px 8px', color: a.is_late ? '#d97706' : '#374151' }}>{a.clock_in || '—'}</td>
                  <td style={{ padding: '9px 8px', color: '#374151' }}>{a.clock_out || (a.clocked_in ? <span style={{color:'#d97706'}}>Still in</span> : '—')}</td>
                  <td style={{ padding: '9px 8px' }}>
                    {a.hours !== null ? <HoursBar hours={a.hours} /> : <span style={{ color: '#dc2626', fontSize: 12 }}>Absent</span>}
                  </td>
                  <td style={{ padding: '9px 0' }}>
                    {!a.clocked_in
                      ? <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Absent</span>
                      : a.is_late
                        ? <span style={{ background: '#fffbeb', color: '#d97706', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Late {a.late_minutes}m</span>
                        : <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>On time</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* Work Log */}
        <Section title="Work Log" emoji="📋">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: '#999', fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600 }}>Entries</th>
                <th style={{ textAlign: 'left', paddingBottom: 8, fontWeight: 600 }}>Clients</th>
              </tr>
            </thead>
            <tbody>
              {data.work_log.map(w => (
                <tr key={w.id} style={{ borderTop: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '9px 0', fontWeight: 600, color: '#111' }}>{w.name}</td>
                  <td style={{ padding: '9px 8px' }}>
                    {w.count === 0
                      ? <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>⚠ No entries</span>
                      : <span style={{ background: '#eff6ff', color: '#073E81', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{w.count}</span>
                    }
                  </td>
                  <td style={{ padding: '9px 0', color: '#555', fontSize: 12 }}>{w.clients.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* Tasks Completed */}
        <Section title="Tasks Completed" emoji="✅">
          {data.tasks_completed.length === 0
            ? <div style={{ color: '#999', fontSize: 13 }}>No tasks marked done on this day.</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.tasks_completed.map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8 }}>
                    <span style={{ color: '#16a34a', fontSize: 16 }}>✓</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{t.title}</div>
                      {t.project_name && <div style={{ fontSize: 11, color: '#666' }}>{t.project_name}</div>}
                    </div>
                    <span style={{ fontSize: 12, color: '#555' }}>{t.assigned_name || t.creator_name || '—'}</span>
                  </div>
                ))}
              </div>
          }
        </Section>

        {/* Overdue Tasks */}
        {data.tasks_overdue.length > 0 && (
          <Section title="Overdue Tasks" emoji="⚠️">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.tasks_overdue.map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fffbeb', borderRadius: 8 }}>
                  <span style={{ color: '#d97706', fontSize: 16 }}>!</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{t.title}</div>
                    {t.project_name && <div style={{ fontSize: 11, color: '#666' }}>{t.project_name}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>Due {new Date(t.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                    {t.assigned_name && <div style={{ fontSize: 11, color: '#888' }}>{t.assigned_name}</div>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

      </>}
    </div>
  );
}
